import React from "react";
import { Button } from "./ui/button";
import { ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Pagination bar with First / Previous / numbered pages / Next / Last controls.
 *
 * Props:
 *   page       - current 0-indexed page
 *   pageSize   - records per page
 *   total      - total record count (across all pages)
 *   onPageChange(newPage)
 *   testidPrefix - optional data-testid prefix
 */
export default function Pagination({ page, pageSize, total, onPageChange, testidPrefix = "pagination" }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  const clamp = p => Math.min(Math.max(0, p), totalPages - 1);
  const go = p => onPageChange(clamp(p));

  // Compute compact page window (max 7 numbers) with ellipsis
  const window = 5;
  let start = Math.max(0, page - Math.floor(window / 2));
  let end = Math.min(totalPages, start + window);
  start = Math.max(0, end - window);
  const numbers = [];
  for (let i = start; i < end; i++) numbers.push(i);

  const showFirstEllipsis = start > 0;
  const showLastEllipsis = end < totalPages;

  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  const btn = "rounded-sm h-8 min-w-8 px-2 text-xs font-heading";

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-3 px-3 border-t bg-white" data-testid={testidPrefix}>
      <div className="text-xs text-slate-500 font-overline tracking-wider">
        Showing <strong className="font-semibold text-slate-700">{from}–{to}</strong> of <strong className="font-semibold text-slate-700">{total}</strong>
      </div>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" className={btn} disabled={page === 0} onClick={() => go(0)} data-testid={`${testidPrefix}-first`} title="First page">
          <ChevronsLeft className="w-3.5 h-3.5"/>
        </Button>
        <Button size="sm" variant="outline" className={btn} disabled={page === 0} onClick={() => go(page - 1)} data-testid={`${testidPrefix}-prev`} title="Previous page">
          <ChevronLeft className="w-3.5 h-3.5"/>
        </Button>
        {showFirstEllipsis && <span className="px-1 text-slate-400">…</span>}
        {numbers.map(n => (
          <Button
            key={n}
            size="sm"
            variant={n === page ? "default" : "outline"}
            className={btn + (n === page ? " bg-[#0A1128] hover:bg-slate-800 text-white" : "")}
            onClick={() => go(n)}
            data-testid={`${testidPrefix}-page-${n + 1}`}
          >
            {n + 1}
          </Button>
        ))}
        {showLastEllipsis && <span className="px-1 text-slate-400">…</span>}
        <Button size="sm" variant="outline" className={btn} disabled={page >= totalPages - 1} onClick={() => go(page + 1)} data-testid={`${testidPrefix}-next`} title="Next page">
          <ChevronRight className="w-3.5 h-3.5"/>
        </Button>
        <Button size="sm" variant="outline" className={btn} disabled={page >= totalPages - 1} onClick={() => go(totalPages - 1)} data-testid={`${testidPrefix}-last`} title="Last page">
          <ChevronsRight className="w-3.5 h-3.5"/>
        </Button>
      </div>
    </div>
  );
}
